import { AGENT_TYPES, type UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { generateAndDownloadSessionReport } from "@/modules/agent/utils/downloadReport";
import { getAgentSession } from "@/modules/sessions/requests";
import { handleContinueMultiVendorAssessment } from "@/modules/sessions/use-cases/agent-session.use-case";
import type { MultiVendorAgentSession } from "@/modules/sessions/types";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ErrorBoundary from "@/shared/components/error-boundary";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { Select, SelectItem } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTableState } from "./hooks/useTableState";
import { ExpandableVendorsTable } from "./components/ExpandableVendorsTable";
import { PageErrorFallback } from "./components/PageErrorFallback";
import { Pagination } from "./components/Pagination";
import { TableErrorState } from "./components/TableErrorState";
import { TableSkeleton } from "./components/TableSkeleton";
import { useGetThirdPartiesQuery } from "./requests";
import type { FilterKey, ActionType, Assessment, AssessmentStatusFilter, BusinessCriticalityFilter } from "./types";
import { isAssessment } from "./types";
import { STATUS_FILTER_OPTIONS, BUSINESS_CRITICALITY_OPTIONS } from "./constants";
import { VersionHistoryDrawer, useVersionHistoryDrawerStore } from "./components/VersionHistoryDrawer";

export default function TPRAVendorsV2Page() {
  return (
    <ErrorBoundary fallback={<PageErrorFallback />}>
      <TPRAVendorsV2PageContent />
    </ErrorBoundary>
  );
}

function TPRAVendorsV2PageContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Version history drawer store
  const openVersionHistoryDrawer = useVersionHistoryDrawerStore((state) => state.openDrawer);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get("assessment_status") || "all",
  );
  const [businessCriticalityFilter, setBusinessCriticalityFilter] = useState<string>("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle URL params on mount
  useEffect(() => {
    const statusParam = searchParams.get("assessment_status");
    const riskParam = searchParams.get("filter_keys");
    const criticalityParam = searchParams.get("business_criticality");
    const searchParam = searchParams.get("search");

    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (riskParam) {
      setRiskFilter(riskParam);
    }
    if (criticalityParam) {
      setBusinessCriticalityFilter(criticalityParam);
    }
    if (searchParam) {
      setSearchTerm(searchParam);
      setDebouncedSearchTerm(searchParam); // Set immediately to trigger API call
    }
  }, [searchParams]);

  // Table state (expansion and sorting)
  const { expandedVendors, toggleVendor, expandAll, sortColumn, sortDirection, setSort } = useTableState({});

  // API query with params (uses debounced search term)
  const { data, isLoading, isError, refetch } = useGetThirdPartiesQuery({
    page: currentPage,
    page_size: pageSize,
    search_key: debouncedSearchTerm.trim() || undefined,
    filter_keys: riskFilter !== "all" ? [riskFilter as FilterKey] : undefined,
    assessment_status: statusFilter !== "all" ? (statusFilter as AssessmentStatusFilter) : undefined,
    business_criticality: businessCriticalityFilter !== "all" ? [businessCriticalityFilter as BusinessCriticalityFilter] : undefined,
    sort_by: sortColumn || undefined,
    sort_order: sortDirection,
  });

  // Use API data
  const totalCount = data?.total_third_parties ?? 0;
  const totalActiveAssessments = data?.total_active_assessments ?? 0;
  const filteredVendors = data?.third_parties ?? [];
  const totalFilteredCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? 0;

  const totalVendors = totalCount;
  const totalAssessments = filteredVendors.reduce((sum, tp) => sum + tp.number_of_assessments, 0);

  // Expand all vendors by default when data loads
  useEffect(() => {
    if (filteredVendors.length > 0) {
      const vendorNames = filteredVendors.map((v) => v.vendor_name);
      expandAll(vendorNames);
    }
  }, [filteredVendors, expandAll]);

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRiskFilterChange = (value: string) => {
    if (!value || value === riskFilter) return;
    setRiskFilter(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    if (!value || value === statusFilter) return;
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleBusinessCriticalityFilterChange = (value: string) => {
    if (!value || value === businessCriticalityFilter) return;
    setBusinessCriticalityFilter(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setRiskFilter("all");
    setStatusFilter("all");
    setBusinessCriticalityFilter("all");
    setCurrentPage(1);
  }, []);

  // Handle assessment actions
  const handleAction = useCallback(
    async (action: ActionType, assessment: Assessment) => {
      switch (action) {
        case "view_assessment":
          if (assessment.externally_filled_intake_form_id) {
            navigate(`/third-party-risk/intake-review/${assessment.externally_filled_intake_form_id}`);
          } else {
            navigate(`/agent/${assessment.id}`);
          }
          break;

        case "download_report":
          try {
            // Fetch session data from API
            const reportResponse = await getAgentSession(assessment.id);
            const { session: reportSessionData, steps: reportSteps } = reportResponse.data;

            // Load session data into store (required for download to work)
            await handleContinueMultiVendorAssessment({
              session: reportSessionData as MultiVendorAgentSession,
              steps: reportSteps,
            });

            // Generate and download with steps
            await generateAndDownloadSessionReport(assessment.id, "pdf", { steps: reportSteps });
          } catch (error) {
            addNotification({
              type: "error",
              title: "Download failed",
              message: "Failed to download report. Please try again.",
            });
          }
          break;

        case "download_summary":
          try {
            // Fetch session data from API
            const summaryResponse = await getAgentSession(assessment.id);
            const { session: summarySessionData, steps: summarySteps } = summaryResponse.data;

            // Load session data into store (required for download to work)
            await handleContinueMultiVendorAssessment({
              session: summarySessionData as MultiVendorAgentSession,
              steps: summarySteps,
            });

            // Generate and download with steps (format is 'summary_pdf' not 'summary')
            await generateAndDownloadSessionReport(assessment.id, "summary_pdf", { steps: summarySteps });
          } catch (error) {
            addNotification({
              type: "error",
              title: "Download failed",
              message: "Failed to download summary. Please try again.",
            });
          }
          break;

        case "reassess":
          // Navigate to assessment page with reassess mode
          navigate(`/agent/${assessment.id}?reassess=true`);
          break;

        case "version_history":
          if (isAssessment(assessment)) {
            openVersionHistoryDrawer({
              vendorName: assessment.multi_file_vendor_assessment.vendor_name,
              productName: assessment.multi_file_vendor_assessment.product_name,
              vendorLogoUrl: assessment.multi_file_vendor_assessment.vendor_logo_url,
            });
          }
          break;

        default:
          break;
      }
    },
    [navigate],
  );

  return (
    <div className="w-full h-full flex flex-col">
      {/* Top Bar: Breadcrumb + New Assessment Button */}
      <div className="flex items-center pb-4 justify-between min-h-[44px]">
        <div className="flex gap-4 items-center">
          <ZBreadCrumbs
            items={[
              { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
              { title: "All Third-Parties", href: `/${ROUTES.THIRD_PARTY_VENDORS_V2}` },
            ]}
          />
        </div>
        <ZButton color="primary" className="text-xs font-medium h-7 px-3" onClick={handleNewAssessment}>
          New Assessment
        </ZButton>
      </div>

      {/* Main Content Card */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-[#E2E8F0] flex flex-col overflow-hidden">
        {/* Header: Title + Count + Subtitle | Risk Filter + Search */}
        <div className="px-6 py-4 flex flex-col bg-white border-b border-[#E4E4E7]">
          <div className="flex flex-row justify-between items-center gap-2">
            {/* Left: Title + Count + Subtitle */}
            <div className="flex flex-col justify-center items-start flex-grow">
              <div className="flex flex-row items-center gap-2">
                <h2 className="font-['Inter'] font-semibold text-base leading-6 text-[#0F172B]">All Third-Parties</h2>
                <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F1F5F9] rounded-[45px]">
                  <span className="font-['Inter'] font-medium text-[10px] leading-4 text-[#45556C]">
                    {totalVendors}
                  </span>
                </div>
              </div>
              <span className="font-['Inter'] font-normal text-xs leading-4 text-[#62748E]">
                {totalActiveAssessments} active assessments
              </span>
            </div>

            {/* Right: Status Filter + Risk Filter + Business Criticality Filter + Search */}
            <div className="flex flex-row items-center gap-2">
              
              <Select
                placeholder="All Risks"
                selectedKeys={[riskFilter]}
                onChange={(e) => handleRiskFilterChange(e.target.value)}
                className="w-[140px]"
                classNames={{
                  trigger: "h-9 bg-white border border-[#E2E8F0] rounded-md",
                  value: "font-['Inter'] font-normal text-sm leading-5 text-[#314158]",
                }}
              >
                <SelectItem key="all" value="all" title="All Risks">
                  All Risks
                </SelectItem>
                <SelectItem key="high" value="high" title="High">
                  High
                </SelectItem>
                <SelectItem key="medium" value="medium" title="Medium">
                  Medium
                </SelectItem>
                <SelectItem key="low" value="low" title="Low">
                  Low
                </SelectItem>
              </Select>
              <Select
                placeholder="Status"
                selectedKeys={[statusFilter]}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="w-[180px]"
                classNames={{
                  trigger: "h-9 bg-white border border-[#E2E8F0] rounded-md",
                  value: "font-['Inter'] font-normal text-sm leading-5 text-[#314158]",
                }}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} title={option.label}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
              <Select
                placeholder="Business Criticality"
                selectedKeys={[businessCriticalityFilter]}
                onChange={(e) => handleBusinessCriticalityFilterChange(e.target.value)}
                className="w-[160px]"
                classNames={{
                  trigger: "h-9 bg-white border border-[#E2E8F0] rounded-md",
                  value: "font-['Inter'] font-normal text-sm leading-5 text-[#314158]",
                }}
              >
                {BUSINESS_CRITICALITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value} title={option.label}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
              <SearchField
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by third-party or product name"
                className="w-[420px] h-9 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Expandable Vendors Table */}
        <div className="flex-1 min-h-0 flex flex-col bg-white overflow-auto p-4">
          <div className="flex-1 min-h-0 flex flex-col border border-[#E4E4E7] rounded-lg overflow-hidden">
            {isLoading ? (
              <TableSkeleton />
            ) : isError ? (
              <TableErrorState onRetry={() => refetch()} />
            ) : (
              <ExpandableVendorsTable
                vendors={filteredVendors}
                expandedVendors={expandedVendors}
                onToggleVendor={toggleVendor}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={setSort}
                onAction={handleAction}
              />
            )}

            {/* Pagination - only show when not loading/error */}
            {totalPages > 0 && !isLoading && !isError && (
              <div className="border-t border-[#E2E8F0]">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />

      {/* Version History Drawer */}
      <VersionHistoryDrawer />
    </div>
  );
}
